import * as Sentry from '@sentry/nextjs';
import { ChangeEvent, Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import useSpeechRecognition from '@/hooks/useSpeechRecognition';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { getCookieValue, isFromVrApp } from '@/utils/helper';
import { noSpecialCharsRegex, withSpecialCharFilter } from '@/utils/validation';
import Mic from './icons/Mic';
import SearchBack from './icons/SearchBack';
import SearchCross from './icons/SearchCross';
import SearchLoader from './icons/searchLoader';
import VoiceListeningModal from './VoiceListeningModal';
interface Props {
  placeholder: string;
  onSearch?: (text: string) => void;
  lang: string;
  initialSearchText?: string;
  onFocus?: VoidFunction;
  showBackBtn?: boolean;
  onBackClick?: VoidFunction;
  setIsTyped?: Dispatch<SetStateAction<boolean>>;
  recentSearches?: string[];
  searchingProducts?: boolean;
  suggestionList?: string[];
  autoFocus?: boolean;
}
interface ButtonWithMicProps {
  showButton: boolean;
  isListening: boolean;
  onStartListening: VoidFunction;
  onStopListening: VoidFunction;
  onFocus?: VoidFunction;
}
const ButtonWithMic = ({
  showButton,
  isListening,
  onStartListening,
  onStopListening
}: ButtonWithMicProps) => {
  const {
    trackCustomEvent
  } = useTriggerEvents();
  return showButton ? <button className={`${isListening ? 'bg-error-10' : ''} rounded-lg flex justify-center p-2.5 items-center bg-primary-10 ml-2`} aria-label="Click for searching via speech" onClick={() => {
    trackCustomEvent(CustomEventTypes.SEARCH_BY_VOICE);
    isListening ? onStopListening() : onStartListening();
  }} data-sentry-component="ButtonWithMic" data-sentry-source-file="NewSearchBox.tsx">
      <Mic className={`${isListening ? 'stroke-error-100' : 'stroke-primary-100'} fill-transparent h-6 w-[25px]`} data-sentry-element="Mic" data-sentry-source-file="NewSearchBox.tsx" />
    </button> : null;
};
export type InputHandle = {
  setInputValue: (newValue: string) => void;
};
const NewSearchBox = forwardRef<InputHandle, Props>(({
  placeholder,
  onSearch,
  onFocus,
  onBackClick,
  lang,
  initialSearchText = '',
  showBackBtn = false,
  setIsTyped,
  recentSearches = [],
  searchingProducts = false,
  suggestionList = [],
  autoFocus = false
}, ref) => {
  const [searchText, setSearchText] = useState(initialSearchText);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const isVR = isFromVrApp(getCookieValue(NAVIGATION_FROM_APP_CODE));
  const {
    onStartListening,
    isListening,
    stopListening,
    isSpeechRecognitionSupported
  } = useSpeechRecognition(setSearchText, lang);
  const crossHandler = () => {
    setSearchText('');
    trackCustomEvent(CustomEventTypes.SEARCH_RESET);
  };
  useImperativeHandle(ref, () => ({
    setInputValue: (newValue: string) => {
      setSearchText(newValue);
    }
  }));
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setIsTyped && setIsTyped(true);
    const value = e.currentTarget.value;
    if (value === 'cmdSentryManualCrash') {
      try {
        throw new Error('This is a manual crash for testing Sentry');
      } catch (error) {
        Sentry.captureException(error);
      }
    } else {
      setSearchText(value);
    }
  }, [setIsTyped]);
  useEffect(() => {
    onSearch?.(searchText);
  }, [searchText, onSearch]);
  const regex = noSpecialCharsRegex;
  const handleChange = useMemo(() => {
    return regex.test(searchText) ? withSpecialCharFilter(onChange, true) : onChange;
  }, [regex, searchText, onChange]);
  return <section className="bg-white m-auto w-full max-w-5xl">
        <div className="flex items-center">
          <div className="flex flex-grow items-center border border-neutral-20 rounded-lg">
            {showBackBtn && <SearchBack className="ml-2 cursor-pointer" onClick={onBackClick} />}
            <input id="search-input" type="text" onChange={handleChange} placeholder={placeholder} className="flex-grow bg-transparent p-3 focus:outline-none text-neutral-90 text-sm" value={searchText} onFocus={onFocus} autoFocus={autoFocus} />
            {searchingProducts ? <SearchLoader className="m-1 animate-spin [animation-duration:1200ms]" /> : searchText ? <SearchCross className="m-1 cursor-pointer" onClick={crossHandler} /> : null}
          </div>

          {!isVR ? <ButtonWithMic showButton={isSpeechRecognitionSupported} onStartListening={onStartListening} onStopListening={stopListening} isListening={isListening} /> : null}
        </div>
        {isListening && <VoiceListeningModal onStopListening={stopListening} isListening={isListening} />}
      </section>;
});
export default NewSearchBox;